// ./src/components/features/EventShare/ShareQrLink.tsx
//
// ROADMAP #138 — ONE link + QR block: the QR (any colour, made at print size), the link, Copy link and Download QR.
// It was written three times — the wizard's finish screen, the dashboard's Share drawer and a channel's QR drawer —
// and the copies had already drifted (220 px vs print size; a colour picker in two of the three; three file names).
//
// ⚠ Everything here is built from the ONE `url` it is given, so the QR, the shown link and the copied link can never
//   disagree about which channel they carry.

import React, { useEffect, useState } from 'react';
import { ActionIcon, Box, Button, ColorPicker, Group, Image, Popover, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { AppIcon } from '@/components/generic';

import { QR_DEFAULT_COLOR, QR_RESOLUTION, QR_SWATCHES } from './eventShareRules';

/** The on-screen size of the QR (the image itself is `QR_RESOLUTION`). */
const QR_SHOWN = 220;

export interface ShareQrLinkProps {
  /** The link to show, copy and encode. Null → nothing to share yet (the buttons are disabled). */
  url: string | null;
  /** The downloaded QR's file name — `qrFileName(...)`. */
  fileName: string;
  /** The QR image's alt text. */
  qrAlt: string;
}

export const ShareQrLink: React.FC<ShareQrLinkProps> = ({ url, fileName, qrAlt }) => {
  const { t } = useTranslation(['events', 'common']);
  const clipboard = useClipboard({ timeout: 1500 });
  const [color, setColor] = useState(QR_DEFAULT_COLOR);
  const [colorOpen, setColorOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setQr(null);

      return;
    }

    let cancelled = false;

    QRCode.toDataURL(url, { width: QR_RESOLUTION, margin: 1, color: { dark: color, light: '#ffffff' } })
      .then((dataUrl) => {
        if (!cancelled) {
          setQr(dataUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQr(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, color]);

  const colorLabel = t('events:share.qrColor', { defaultValue: 'QR color' });

  return (
    <Stack gap="md" align="center" w="100%">
      {/* The palette sits ABOVE the QR, right-aligned — never over the code, so it stays scannable. */}
      <Stack gap={8} align="center" w={QR_SHOWN}>
        <Group justify="flex-end" w="100%" gap={6}>
          <Text size="md" c="dimmed">
            {colorLabel}
          </Text>
          <Popover opened={colorOpen} onChange={setColorOpen} position="bottom-end" withArrow shadow="md" trapFocus>
            <Popover.Target>
              <ActionIcon variant="default" size="lg" radius="md" onClick={() => setColorOpen((o) => !o)} aria-label={colorLabel}>
                <AppIcon iconName="Palette" size={18} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <ColorPicker format="hex" value={color} onChange={setColor} swatches={QR_SWATCHES} swatchesPerRow={6} />
            </Popover.Dropdown>
          </Popover>
        </Group>
        <Box w={QR_SHOWN} h={QR_SHOWN}>
          {qr ? <Image src={qr} alt={qrAlt} w={QR_SHOWN} h={QR_SHOWN} /> : null}
        </Box>
      </Stack>

      <TextInput
        readOnly
        w="100%"
        size="md"
        value={url ?? ''}
        leftSection={<AppIcon iconName="World" size={16} />}
        onFocus={(e) => e.currentTarget.select()}
        aria-label={t('events:share.linkLabel', { defaultValue: 'Event link' })}
      />

      {/* Side by side from `xs` up; stacked on a phone, where two md labels do not fit one row ("Download QR" clipped). */}
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm" w="100%">
        <Button
          variant="light"
          size="md"
          fullWidth
          disabled={!url}
          leftSection={<AppIcon iconName={clipboard.copied ? 'Check' : 'Copy'} size={16} />}
          onClick={() => {
            if (url) {
              clipboard.copy(url);
            }
          }}
        >
          {clipboard.copied ? t('common:copied', { defaultValue: 'Copied' }) : t('events:share.copyLink', { defaultValue: 'Copy link' })}
        </Button>
        <Button
          variant="light"
          size="md"
          fullWidth
          component="a"
          href={qr ?? undefined}
          download={fileName}
          disabled={!qr}
          leftSection={<AppIcon iconName="Download" size={16} />}
        >
          {t('events:share.downloadQr', { defaultValue: 'Download QR' })}
        </Button>
      </SimpleGrid>
    </Stack>
  );
};
